<template>
  <div class="document-container" style="padding-bottom: 0;">
    <div class="document">
      <div class="is-flex">
        <div class="header-icon is-flex flex-align--center flex-justify--center">
          <font-awesome-icon
            :icon="['fa', 'user-circle']"
            class="has-text-grey-light"
            size="4x"
          />
        </div>
        <div class="person-header--overview is-flex flex-direction--column">
          <div class="person-header--person is-flex">
            <div class="is-flex flex-direction--column flex-justify--center has-text-left">
              <strong class="is-size-3">
                {{ person.firstName }} {{ person.middleName }} {{ person.lastName}}
              </strong>
              <div>
                <span class="has-text-grey">
                  {{ personTypeNames.length === 1 ? 'Person Type' : 'Person Types' }}:
                  <span v-for="(personType, index) in personTypeNames" :key="index">
                    {{ personType }}<span v-if="index < personTypeNames.length - 1">,</span>
                    <span v-else>.</span>
                  </span>
                </span>
              </div>
            </div>
            <div v-if="getFieldEnabledForPersonType('STATE_STUDENT_ID',
                'ASSIGN', person.personType)"
                class="is-flex flex-direction--column flex-justify--center">
              <strong v-if="isRetired" class="is-size-4">
                {{ formatStateID(person.personId) }}
                <span style="color: #dc3545">
                  RETIRED
                </span>
              </strong>
              <strong v-else class="is-size-4">
                {{ formatStateID(person.stateStudentId) }}
              </strong>
              <span class="has-text-grey">
                {{
                  getFieldLabel(
                    `PER:${this.person.personType}:${activeModule.spec}:STATE_STUDENT_ID`
                  )
                }}
              </span>
            </div>
          </div>
          <div class="person-header--info columns">
            <div class="column">
              <div v-if="person.createdDate" class="has-text-weight-bold is-size-4">
                {{
                  new Intl.DateTimeFormat('en', {
                    year:'numeric',
                    month:'2-digit',
                    day:'2-digit',
                  })
                  .format(new Date(person.createdDate))
                }}
              </div>
              <div v-else class="has-text-weight-bold">
                --
              </div>
              <div class="is-size-7 has-text-grey">
                Created
              </div>
            </div>
            <div class="column">
              <div v-if="person.lastUpdateDate" class="has-text-weight-bold is-size-4">
                {{
                  new Intl.DateTimeFormat('en', {
                    year:'numeric',
                    month:'2-digit',
                    day:'2-digit',
                  })
                  .format(new Date(person.lastUpdateDate))
                }}
              </div>
              <div v-else class="has-text-weight-bold">
                --
              </div>
              <div class="is-size-7 has-text-grey">
                Last Updated
              </div>
            </div>
            <div class="column">
              <div class="has-text-weight-bold is-size-4">
                <router-link
                  v-if="historyRecordsCount
                    && hasModule('PRSID:PRPRF:PROFILE:VIEW')"
                  :to="{
                    name: 'PidHistoryRecords',
                    query: {
                      retired: isRetired,
                    },
                  }"
                >
                  {{ historyRecordsCount }}
                </router-link>
                <span v-if="historyRecordsCount
                  && !hasModule('PRSID:PRPRF:PROFILE:VIEW')"
                >
                  {{ historyRecordsCount }}
                </span>
                <span v-if="!historyRecordsCount">
                  --
                </span>
              </div>
              <div class="is-size-7 has-text-grey">
                History Records
              </div>
            </div>
            <div class="column">
              <div class="has-text-weight-bold is-size-4">
                <router-link
                  v-if="mergedRecordsCount
                    && hasModule('PRSID:PRPRF:PROFILE:VIEW')"
                  :to="{
                    name: 'PidAssociatedRecords',
                    query: {
                      retired: isRetired,
                    },
                  }"
                >
                  {{ mergedRecordsCount }}
                </router-link>
                <span v-if="mergedRecordsCount
                  && !hasModule('PRSID:PRPRF:PROFILE:VIEW')"
                >
                  {{ mergedRecordsCount }}
                </span>
                <span v-if="!mergedRecordsCount">
                  --
                </span>
              </div>
              <div class="is-size-7 has-text-grey">
                Associated Records
              </div>
            </div>
            <div class="column">
              <div v-if="person.schoolYear" class="has-text-weight-bold is-size-4">
                {{ person.schoolYear }}
              </div>
              <div v-else class="has-text-weight-bold">
                --
              </div>
              <div class="is-size-7 has-text-grey">
                {{
                  getFieldLabel(`PER:${this.person.personType}:${activeModule.spec}:SCHOOL_YEAR`)
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="tabs-container no-border-top no-bottom-border">
        <es-tabs :tabs="filteredRouteLinks" ref="tabs"></es-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import FieldLabels from '@/mixins/FieldLabels';
import PermissionManager from '@/mixins/PermissionManager';
import StateIdUtils from '@/mixins/StateIdUtils';
import { mapGetters } from 'vuex';
import EsTabs from '../../components/EsTabs.vue';

export default {
  mixins: [
    FieldLabels,
    PermissionManager,
    StateIdUtils,
  ],
  components: {
    EsTabs,
  },
  props: {
    dmNearMatchTotalCount: {
      type: Number,
    },
    historyRecordsCount: {
      type: Number,
    },
    isRetired: {
      type: Boolean,
      default: false,
    },
    mergedRecordsCount: {
      type: Number,
    },
    person: {
      type: Object,
      required: true,
    },
    personTypeNames: {
      type: Array,
      required: true,
    },
    pidNearMatchTotalCount: {
      type: Number,
    },
  },
  data() {
    return {
      routeLinks: [
        {
          module: 'directMatch',
          permission: 'DRMCH:PRPRF:PROFILE:VIEW',
          name: 'DmOverview',
          label: 'OVERVIEW',
        },
        {
          module: 'personId',
          permission: 'PRSID:PRPRF:PROFILE:VIEW',
          name: 'PidOverview',
          label: 'OVERVIEW',
        },
        {
          module: 'directMatch',
          permission: 'DRMCH:PRPRF:PROFILE:VIEW',
          name: 'DmMasterRecord',
          label: 'MASTER RECORD',
        },
        {
          module: 'personId',
          permission: 'PRSID:PRPRF:PROFILE:VIEW',
          name: 'PidMasterRecord',
          label: 'MASTER RECORD',
        },
        {
          module: 'directMatch',
          permission: 'DRMCH:PRPRF:PROFILE:VIEW',
          name: 'DmHistoryRecords',
          label: 'HISTORY RECORD',
        },
        {
          module: 'personId',
          permission: 'PRSID:PRPRF:PROFILE:VIEW',
          name: 'PidHistoryRecords',
          label: 'HISTORY RECORD',
        },
        {
          module: 'directMatch',
          permission: 'DRMCH:PRPRF:PROFILE:VIEW',
          name: 'DmAssociatedRecords',
          label: 'ASSOCIATED RECORD',
        },
        {
          module: 'personId',
          permission: 'PRSID:PRPRF:PROFILE:VIEW',
          name: 'PidAssociatedRecords',
          label: 'ASSOCIATED RECORD',
        },
        {
          module: 'directMatch',
          permission: 'DRMCH:PRPRF:PROFILE:VIEW',
          name: 'DmPersonProfileDirectMatch',
          label: 'DIRECT MATCH',
        },
        {
          module: 'personId',
          permission: 'PRSID:PRPRF:PROFILE:VIEW',
          name: 'PidPersonProfileDirectMatch',
          label: 'DIRECT MATCH',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      activeModule: 'appModules/getActiveModule',
    }),
    filteredRouteLinks() {
      return this.routeLinks.filter(route => (
        (this.activeModule.name === route.module && this.hasModule(route.permission))
      )).map((route) => {
        return {
          ...route,
          query: {
            retired: this.isRetired,
          },
        };
      });
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "~bulma/sass/utilities/initial-variables.sass"
  @import "~bulma/sass/utilities/mixins.sass"

  .header-icon
    padding: 1rem
  .person-header--overview
    flex-grow: 1
    margin-right: 1rem
  .person-header--person
    padding: 0.5rem 1rem
    justify-content: space-between
    border-bottom: 1px solid $grey-lighter
    flex-wrap: wrap
  .person-header--info
    margin: 1rem !important
  @include from($tablet)
    .column
      border-left: 1px solid $grey-lighter
    .column:nth-child(1)
      border: none
</style>
